
(() => {
  'use strict';

  const MOBILE_BREAKPOINT = 600; // below this the popup becomes a bottom sheet
  const GAP = 10; // space between anchor and popup
  const EDGE = 12; // minimum breathing room from the viewport edge
  const ARROW_INSET = 10; // how close the arrow may sit to a panel corner
  const MIN_LIST_HEIGHT = 90; // never shrink the bullet list below this

  const SIDES = ['top', 'bottom', 'left', 'right'];

  const PANEL_MARKUP = `
    <div class="ab-toast__panel">
      <span class="ab-toast__media" data-media hidden><img alt="" data-media-img></span>
      <p class="ab-toast__title" data-title></p>
      <p class="ab-toast__message" data-message></p>
      <ul class="ab-toast__points" data-points hidden></ul>
      <div class="ab-toast__actions" data-actions></div>
    </div>
  `;

  /** The single <dialog>, built on first use and reused from then on. */
  let dialog = null;

  /** Resolver for the promise the currently open popup handed its caller. */
  let resolveCurrent = null;

  /** Shorthand for reaching into the dialog. Only valid once it is built. */
  const find = (selector) => dialog.querySelector(selector);

  /** Builds the dialog the first time something asks for it. */
  const getDialog = () => {
    if (dialog) {
      return dialog;
    }

    dialog = document.createElement('dialog');
    dialog.className = 'ab-toast';
    dialog.innerHTML = PANEL_MARKUP;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        settle(false);
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      settle(false);
    });

    document.body.appendChild(dialog);
    return dialog;
  };

  const fitToViewport = (panel, anchor) => {
    const points = find('[data-points]');

    points.style.maxHeight = '';

    const target = anchor.getBoundingClientRect();
    const roomBelow = window.innerHeight - target.bottom - GAP - EDGE;
    const roomAbove = target.top - GAP - EDGE;
    const budget = Math.max(roomBelow, roomAbove);
    const height = panel.getBoundingClientRect().height;

    if (height <= budget) {
      return;
    }

    const listHeight = points.getBoundingClientRect().height;
    points.style.maxHeight = `${Math.max(MIN_LIST_HEIGHT, listHeight - (height - budget))}px`;
  };

  const clearArrow = (panel) => {
    SIDES.forEach((side) => dialog.classList.remove(`ab-toast--arrow-${side}`));
    panel.style.removeProperty('--ab-toast-arrow');
  };

  const pointArrow = (panel, target, left, top, size) => {
    let side = '';

    if (target.bottom <= top) {
      side = 'top';
    } else if (target.top >= top + size.height) {
      side = 'bottom';
    } else if (target.right <= left) {
      side = 'left';
    } else if (target.left >= left + size.width) {
      side = 'right';
    }

    if (!side) {
      clearArrow(panel);
      return;
    }

    const isVertical = side === 'top' || side === 'bottom';

    const centre = isVertical
      ? target.left + target.width / 2 - left
      : target.top + target.height / 2 - top;

    const limit = (isVertical ? size.width : size.height) - ARROW_INSET;
    const offset = Math.round(Math.max(ARROW_INSET, Math.min(centre, limit)));

    SIDES.forEach((name) => {
      dialog.classList.toggle(`ab-toast--arrow-${name}`, name === side);
    });

    panel.style.setProperty('--ab-toast-arrow', `${offset}px`);
  };

  const position = (anchor, align) => {
    const panel = find('.ab-toast__panel');

    if (window.innerWidth <= MOBILE_BREAKPOINT || !anchor) {
      dialog.classList.add('ab-toast--sheet');
      dialog.style.top = '';
      dialog.style.left = '';
      clearArrow(panel);
      return;
    }

    dialog.classList.remove('ab-toast--sheet');

    fitToViewport(panel, anchor);

    const target = anchor.getBoundingClientRect();
    const size = panel.getBoundingClientRect();

    const roomBelow = window.innerHeight - target.bottom - GAP - EDGE;
    const roomAbove = target.top - GAP - EDGE;
    const maxTop = window.innerHeight - size.height - EDGE;
    const maxLeft = window.innerWidth - size.width - EDGE;

    let top;
    let left;
    let flip = false;

    if (size.height <= roomBelow || size.height <= roomAbove) {
      flip = size.height > roomBelow;
      top = flip ? target.top - size.height - GAP : target.bottom + GAP;

      if (align === 'end') {
        left = target.right - size.width;
      } else if (align === 'start') {
        left = target.left;
      } else {
        left = target.left + target.width / 2 - size.width / 2;
      }
    } else {
      top = target.top + target.height / 2 - size.height / 2;
      left = target.right + GAP;

      if (left > maxLeft) {
        left = target.left - size.width - GAP;
      }
    }

    top = Math.max(EDGE, Math.min(top, maxTop));
    left = Math.max(EDGE, Math.min(left, maxLeft));

    dialog.classList.toggle('ab-toast--flip', flip);

    dialog.style.top = `${Math.round(top)}px`;
    dialog.style.left = `${Math.round(left)}px`;

    pointArrow(panel, target, left, top, size);
  };


  const fillLine = (selector, text) => {
    const node = find(selector);
    node.textContent = text || '';
    node.hidden = !text;
  };

  const fillImage = (url) => {
    const frame = find('[data-media]');
    const image = find('[data-media-img]');

    if (url) {
      image.src = url;
      frame.hidden = false;
    } else {
      image.removeAttribute('src');
      frame.hidden = true;
    }

    dialog.classList.toggle('ab-toast--media', Boolean(url));
  };

  const fillPoints = (points) => {
    const list = points || [];
    const node = find('[data-points]');

    node.replaceChildren(
      ...list.map((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      })
    );

    node.hidden = list.length === 0;
  };

  const fillActions = (buttons) => {
    const node = find('[data-actions]');

    node.replaceChildren(
      ...buttons.map(({ label, value, primary }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ab-toast__btn${primary ? ' ab-toast__btn--primary' : ''}`;
        button.textContent = label;
        button.addEventListener('click', () => settle(value));
        return button;
      })
    );
  };

  const open = ({ anchor, align, title, message, image, points, tone, buttons }) => {
    const el = getDialog();

    el.classList.toggle('ab-toast--danger', tone === 'danger');

    fillLine('[data-title]', title);
    fillLine('[data-message]', message);
    fillImage(image);
    fillPoints(points);
    fillActions(buttons);

    el.showModal();
    position(anchor, align);

    const actions = find('[data-actions]');
    const primary = actions.querySelector('.ab-toast__btn--primary') || actions.firstElementChild;

    if (primary) {
      primary.focus();
    }

    return new Promise((resolve) => {
      resolveCurrent = resolve;
    });
  };

  const settle = (value) => {
    if (!dialog || !dialog.open) {
      return;
    }

    find('[data-points]').style.maxHeight = '';

    dialog.close();

    if (resolveCurrent) {
      resolveCurrent(value);
    }

    resolveCurrent = null;
  };


  window.AbToast = {
    confirm: (options = {}) =>
      open({
        ...options,
        buttons: [
          { label: options.cancelText || 'Cancel', value: false },
          { label: options.confirmText || 'Confirm', value: true, primary: true }
        ]
      }),

    notify: (options = {}) =>
      open({
        ...options,
        buttons: [{ label: options.dismissText || 'Got it', value: true, primary: true }]
      }),

    close: () => settle(false)
  };
})();
